/**
 * Storefront promo-code helpers (shared by package card + basket).
 */

export type PromoCodeApplyTo = "PACKAGE" | "LICENSE" | "HOSTING" | "PLAN";
export type PromoCodePackageTier = "BASIC" | "STANDARD" | "PREMIUM";

export type AppliedPromoCode = {
	code: string;
	label: string;
	discountType: "PERCENTAGE" | "FIXED_AMOUNT";
	discountValue: number;
	currency: string;
	applyTo: PromoCodeApplyTo[];
	packageTiers: PromoCodePackageTier[];
	discountAmount: number;
	/** When false/undefined, exclusive — cannot sit beside other promo codes. */
	stackableWithOtherPromoCodes?: boolean;
	/** When true, reduce Due today first; otherwise keep Due today and cut total / later. */
	applyDiscountToDueTodayFirst?: boolean;
};

export type ValidatePromoCodeResponse = AppliedPromoCode & {
	valid: true;
	description?: string;
	eligibleBase?: number;
	oneTimePerUser?: boolean;
	newUsersOnly?: boolean;
};

export type PromoLineAmounts = {
	packageAmount: number;
	licenseAmount: number;
	hostingAmount: number;
	planAmount: number;
};

export function resolvePackageTierFromTitle(
	title?: string | null,
): PromoCodePackageTier | null {
	const t = String(title || "")
		.trim()
		.toLowerCase();
	if (!t) return null;
	if (/\bbasic\b/.test(t)) return "BASIC";
	if (/\bstandard\b/.test(t)) return "STANDARD";
	if (/\bpremium\b/.test(t)) return "PREMIUM";
	return null;
}

function roundMoney(n: number): number {
	return Math.round(Math.max(0, n) * 100) / 100;
}

/**
 * Split Due today / Pay later after promo.
 * - applyToDueTodayFirst true: discount hits Due today first, leftover reduces Pay later.
 * - false (default): Due today stays the same; discount comes out of total / Pay later.
 */
export function applyPromoToDueTodayFirst(
	dueTodayBeforePromo: number,
	totalAfterPromo: number,
	promoDiscountAmount: number,
	applyToDueTodayFirst = false,
): { dueToday: number; payLater: number } {
	const dueBase = roundMoney(dueTodayBeforePromo);
	const total = roundMoney(totalAfterPromo);
	if (applyToDueTodayFirst) {
		const promo = roundMoney(promoDiscountAmount);
		const promoOnDue = Math.min(promo, dueBase);
		const dueToday = Math.min(roundMoney(dueBase - promoOnDue), total);
		const payLater = roundMoney(Math.max(0, total - dueToday));
		return { dueToday, payLater };
	}
	const dueToday = Math.min(dueBase, total);
	const payLater = roundMoney(Math.max(0, total - dueToday));
	return { dueToday, payLater };
}

/** Apply each promo’s Due-today preference in order against a running total. */
export function applyPromosToInstallments(
	dueTodayBeforePromo: number,
	totalBeforePromo: number,
	promos: Pick<
		AppliedPromoCode,
		"discountAmount" | "applyDiscountToDueTodayFirst"
	>[],
): { dueToday: number; payLater: number } {
	let due = roundMoney(dueTodayBeforePromo);
	let total = roundMoney(totalBeforePromo);
	for (const promo of promos) {
		const discount = roundMoney(Math.max(0, promo.discountAmount));
		total = roundMoney(Math.max(0, total - discount));
		if (promo.applyDiscountToDueTodayFirst) {
			const take = Math.min(discount, due);
			due = roundMoney(due - take);
		}
		due = Math.min(due, total);
	}
	return {
		dueToday: due,
		payLater: roundMoney(Math.max(0, total - due)),
	};
}

/** Subtract a promo discount from the line amounts it applies to (proportional). */
export function reduceAmountsByPromoDiscount(
	amounts: PromoLineAmounts,
	applyTo: PromoCodeApplyTo[],
	discountAmount: number,
): PromoLineAmounts {
	const targets = applyTo?.length ? applyTo : (["PACKAGE"] as PromoCodeApplyTo[]);
	const weights: Partial<Record<PromoCodeApplyTo, number>> = {};
	let weightSum = 0;
	for (const key of targets) {
		const w =
			key === "PACKAGE"
				? amounts.packageAmount
				: key === "LICENSE"
					? amounts.licenseAmount
					: key === "HOSTING"
						? amounts.hostingAmount
						: amounts.planAmount;
		const positive = Math.max(0, w);
		weights[key] = positive;
		weightSum += positive;
	}
	if (weightSum <= 0 || discountAmount <= 0) return amounts;

	const next = { ...amounts };
	let remaining = roundMoney(discountAmount);
	const keys = targets.filter((k) => (weights[k] ?? 0) > 0);
	keys.forEach((key, index) => {
		const w = weights[key] ?? 0;
		const share =
			index === keys.length - 1
				? remaining
				: roundMoney((discountAmount * w) / weightSum);
		const take = Math.min(share, remaining, w);
		remaining = roundMoney(remaining - take);
		if (key === "PACKAGE") next.packageAmount = roundMoney(next.packageAmount - take);
		else if (key === "LICENSE") next.licenseAmount = roundMoney(next.licenseAmount - take);
		else if (key === "HOSTING") next.hostingAmount = roundMoney(next.hostingAmount - take);
		else next.planAmount = roundMoney(next.planAmount - take);
	});
	return next;
}

/** Instant client-side discount refresh when cart amounts/tiers change (no API round-trip). */
export function computeAppliedPromoDiscount(params: {
	promo: Pick<
		AppliedPromoCode,
		"discountType" | "discountValue" | "applyTo" | "packageTiers"
	>;
	packageAmount: number;
	licenseAmount?: number;
	hostingAmount?: number;
	planAmount?: number;
	orderAmount: number;
	cartPackageTiers?: PromoCodePackageTier[];
}): { ok: true; discountAmount: number } | { ok: false } {
	const packageAmount = Math.max(0, Number(params.packageAmount) || 0);
	const licenseAmount = Math.max(0, Number(params.licenseAmount) || 0);
	const hostingAmount = Math.max(0, Number(params.hostingAmount) || 0);
	const planAmount = Math.max(0, Number(params.planAmount) || 0);
	const orderAmount = Math.max(0, Number(params.orderAmount) || 0);
	const applyTo = params.promo.applyTo?.length
		? params.promo.applyTo
		: (["PACKAGE"] as PromoCodeApplyTo[]);
	const allowedTiers = params.promo.packageTiers?.length
		? params.promo.packageTiers
		: (["BASIC", "STANDARD", "PREMIUM"] as PromoCodePackageTier[]);
	const cartTiers = params.cartPackageTiers ?? [];

	let eligibleBase = 0;
	if (applyTo.includes("PACKAGE")) {
		const tierOk =
			cartTiers.length === 0 ||
			cartTiers.some((t) => allowedTiers.includes(t)) ||
			allowedTiers.length === 0;
		if (!tierOk) return { ok: false };
		eligibleBase += packageAmount;
	}
	if (applyTo.includes("LICENSE")) eligibleBase += licenseAmount;
	if (applyTo.includes("HOSTING")) eligibleBase += hostingAmount;
	if (applyTo.includes("PLAN")) eligibleBase += planAmount;
	eligibleBase = roundMoney(eligibleBase);
	if (eligibleBase <= 0) return { ok: false };

	let discountAmount = 0;
	if (params.promo.discountType === "PERCENTAGE") {
		discountAmount = roundMoney(
			eligibleBase * (Math.min(100, params.promo.discountValue) / 100),
		);
	} else {
		discountAmount = roundMoney(Math.min(params.promo.discountValue, eligibleBase));
	}
	discountAmount = Math.min(discountAmount, orderAmount);
	if (discountAmount <= 0) return { ok: false };
	return { ok: true, discountAmount };
}

/**
 * Recompute stacked promos in order against cart amounts.
 * Each subsequent code sees remaining line amounts after earlier discounts.
 */
export function recomputeStackedPromos(params: {
	promos: AppliedPromoCode[];
	packageAmount: number;
	licenseAmount?: number;
	hostingAmount?: number;
	planAmount?: number;
	orderAmount: number;
	cartPackageTiers?: PromoCodePackageTier[];
}): { ok: true; promos: AppliedPromoCode[]; totalDiscount: number } | { ok: false } {
	if (!params.promos.length) {
		return { ok: true, promos: [], totalDiscount: 0 };
	}

	let amounts: PromoLineAmounts = {
		packageAmount: Math.max(0, Number(params.packageAmount) || 0),
		licenseAmount: Math.max(0, Number(params.licenseAmount) || 0),
		hostingAmount: Math.max(0, Number(params.hostingAmount) || 0),
		planAmount: Math.max(0, Number(params.planAmount) || 0),
	};
	let remainingOrder = Math.max(0, Number(params.orderAmount) || 0);
	const next: AppliedPromoCode[] = [];
	let totalDiscount = 0;

	for (let i = 0; i < params.promos.length; i++) {
		const promo = params.promos[i];
		if (i > 0) {
			const allStackable =
				Boolean(promo.stackableWithOtherPromoCodes) &&
				next.every((p) => Boolean(p.stackableWithOtherPromoCodes));
			if (!allStackable) return { ok: false };
		}

		const computed = computeAppliedPromoDiscount({
			promo,
			...amounts,
			orderAmount: remainingOrder,
			cartPackageTiers: params.cartPackageTiers,
		});
		if (!computed.ok) return { ok: false };

		next.push({ ...promo, discountAmount: computed.discountAmount });
		totalDiscount = roundMoney(totalDiscount + computed.discountAmount);
		amounts = reduceAmountsByPromoDiscount(
			amounts,
			promo.applyTo,
			computed.discountAmount,
		);
		remainingOrder = roundMoney(remainingOrder - computed.discountAmount);
	}

	return { ok: true, promos: next, totalDiscount };
}

export function sumPromoDiscount(promos: AppliedPromoCode[]): number {
	return roundMoney(
		promos.reduce((sum, p) => sum + Math.max(0, Number(p.discountAmount) || 0), 0),
	);
}

export function canAddAnotherPromo(applied: AppliedPromoCode[]): boolean {
	if (applied.length === 0) return true;
	return applied.every((p) => Boolean(p.stackableWithOtherPromoCodes));
}
